import { Box } from "../../ui/box";

/**
 * The six facets of the mark, in fixed brand colors. These are Montu's emblem
 * geometry and palette — a fixed identity asset, not a themeable UI color, so
 * (like the logo PNGs elsewhere in the app) they are pinned to their exact
 * hex values rather than routed through the Tailwind theme scale.
 */
const FACETS: { d: string; fill: string }[] = [
  {
    d: "M 0 140 L 107.3 32.8 A 78 78 0 0 1 217.7 32.8 L 325 140 L 162.5 302.5 Z",
    fill: "#FFAD29",
  },
  {
    d: "M 325 140 L 432.3 32.8 A 78 78 0 0 1 542.7 32.8 L 650 140 L 487.5 302.5 Z",
    fill: "#FFAD29",
  },
  { d: "M 0 140 L 162.5 302.5 L 0 465 Z", fill: "#591DFF" },
  { d: "M 650 140 L 487.5 302.5 L 650 465 Z", fill: "#591DFF" },
  { d: "M 162.5 302.5 L 325 465 L 0 465 Z", fill: "#FF22B5" },
  { d: "M 487.5 302.5 L 650 465 L 325 465 Z", fill: "#FF22B5" },
];

type Props = {
  /** Rendered width, in px. Height follows the mark's own aspect ratio. */
  size: number;
  /** "Glass emblem, thinking": every facet dims and a light pulse travels the
   * shape. Off renders the static, full-color mark. */
  thinking?: boolean;
};

/**
 * Montu's emblem, inline.
 *
 * Raw DOM `<svg>`, not `react-native-svg` — same call as `PricingSparkline`: this
 * only ever renders on web (the agent kit is center-only today), and
 * `react-native-svg`'s web types carry no `className`/inline `style` on `<path>`,
 * which the thinking pulse below needs.
 *
 * Thinking mode dims every facet to ~0.3 opacity and animates each one back up to
 * full and down again on a shared 2.4s loop, staggered by `index * 0.4s` so the
 * pulse reads as a light crossing the mark rather than the whole thing flashing at
 * once — no rotation, this is a breathing glass object, not a spinner.
 * `motion-reduce` collapses back to the static, full-color, non-pulsing mark.
 */
const MontuEmblem = ({ size, thinking = false }: Props) => {
  const height = (size * 465) / 650;

  return (
    <Box style={{ width: size, height }}>
      <svg viewBox="0 0 650 465" width={size} height={height}>
        {FACETS.map((facet, index) => (
          <path
            key={facet.d}
            d={facet.d}
            fill={facet.fill}
            className={
              thinking
                ? "animate-facet-pulse motion-reduce:animate-none motion-reduce:opacity-100"
                : undefined
            }
            style={
              thinking
                ? { opacity: 0.3, animationDelay: `${index * 0.4}s` }
                : undefined
            }
          />
        ))}
      </svg>
    </Box>
  );
};

export default MontuEmblem;
